import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { useCurrentUserStore } from '@store'

/** « Garder contact »: at the time of the follow-up, the same day at 9:00, or never. */
export type KeepContactAlert = 'onTime' | 'morning' | 'off'
/** « Anniversaires »: the day before and on the day, on the day, or never. */
export type BirthdayAlert = 'eveAndDay' | 'day' | 'off'
/** « Rendez-vous »: minutes before, or never. */
export type EventAlert = '15' | '30' | '60' | '120' | 'off'
/** « Nouveaux messages », « Demandes de contact ». */
export type LiveAlert = 'always' | 'exceptNight' | 'off'
/** « Résumé du matin »: none, or every day at this hour. */
export type MorningDigest = 'off' | '7' | '8' | '9'
/** « Ne pas déranger »: off, or from one hour to the other. */
export type QuietHours = 'off' | '21-7' | '22-7' | '22-8' | '23-7' | '23-8'

export interface NotificationSettings {
  keepContact: KeepContactAlert
  birthdays: BirthdayAlert
  events: EventAlert
  messages: LiveAlert
  requests: LiveAlert
  morningDigest: MorningDigest
  quietHours: QuietHours
}

export const NOTIFICATION_SETTINGS_QUERY_KEY = ['notification-settings'] as const

const fetchNotificationSettings = async (): Promise<NotificationSettings> => {
  const { data } = await api.get<NotificationSettings>('/notification-settings')
  return data
}

const saveNotificationSettings = async (
  settings: Partial<NotificationSettings>,
): Promise<NotificationSettings> => {
  const { data } = await api.put<NotificationSettings>('/notification-settings', settings)
  return data
}

/** « Réglages des notifications » of the signed-in person. */
export const useNotificationSettings = () => {
  const { userID } = useCurrentUserStore()
  return useQuery({
    queryKey: NOTIFICATION_SETTINGS_QUERY_KEY,
    queryFn: fetchNotificationSettings,
    enabled: !!userID,
  })
}

export const useUpdateNotificationSettings = () => {
  const queryClient = useQueryClient()
  return useMutation<NotificationSettings, Error, Partial<NotificationSettings>>({
    mutationFn: saveNotificationSettings,
    onSuccess: (settings) => {
      queryClient.setQueryData(NOTIFICATION_SETTINGS_QUERY_KEY, settings)
    },
  })
}
